/**
 * RetireSessionsDialog — the one user-facing way to "delete" sessions.
 *
 * Migration 758: deleting a session is no longer a permanent delete. It is a
 * governed Retire (`retire_session`) — canon 11 §2.1 Tier 1: the session and
 * everything under it (scores, results, notes) stay, every list hides it, and
 * it can be taken back from its own page. One written reason covers the whole
 * selection. Sessions something already depends on (published results, a
 * proposal, a priority list, a decision, a track's official result, a loop, a
 * follow-on session, a live group decision) are refused by the server, which
 * names what blocks each one and what to do instead.
 *
 * Built on the shared ds `Modal`, like RetireItemsDialog (its model).
 */
import { useEffect, useState } from 'react';
import { Modal, TextArea, Callout } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { useSessionStore } from '@/stores/prioritization/sessionStore';

export interface RetireSessionsDialogProps {
  open: boolean;
  onClose: () => void;
  sessions: Array<{ id: string; name?: string | null }>;
  /** Called with the ids that were actually retired (may be a subset). */
  onRetired?: (retiredIds: string[]) => void;
}

interface Failure {
  id: string;
  name: string;
  message: string;
}

export function RetireSessionsDialog({ open, onClose, sessions, onRetired }: RetireSessionsDialogProps) {
  const retireSession = useSessionStore((s) => s.retireSession);
  const { showToast } = useToast();

  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failures, setFailures] = useState<Failure[]>([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setFailures([]);
      setDone(false);
    }
  }, [open]);

  const count = sessions.length;
  const single = count === 1;
  const label = single ? (sessions[0]?.name?.trim() || 'this session') : `${count} sessions`;
  const trimmed = reason.trim();

  const handleRetire = async () => {
    if (!trimmed || busy) return;
    setBusy(true);
    const retired: string[] = [];
    const failed: Failure[] = [];
    try {
      for (const s of sessions) {
        const result = await retireSession(s.id, trimmed);
        const name = s.name?.trim() || 'Untitled session';
        if (result.ok) {
          retired.push(s.id);
        } else {
          const why = result.error ?? 'refused';
          failed.push({ id: s.id, name, message: result.remedy ? `${why} — ${result.remedy}` : why });
        }
      }
    } finally {
      setBusy(false);
    }
    if (retired.length > 0) onRetired?.(retired);
    if (failed.length === 0) {
      showToast(
        'success',
        single ? 'Session retired' : 'Sessions retired',
        single
          ? `“${label}” left every list. Its record stays, and you can take it back from its page.`
          : `Retired ${retired.length} sessions. Their records stay.`,
      );
      onClose();
      return;
    }
    setFailures(failed);
    setDone(true);
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title={single ? 'Retire this session' : `Retire ${count} sessions`}
      dismissible={!busy}
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {done ? 'Close' : 'Cancel'}
          </Button>
          {!done && (
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => void handleRetire()}
              disabled={busy || !trimmed}
            >
              {busy ? 'Retiring…' : single ? 'Retire session' : `Retire ${count} sessions`}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-[var(--text-sec)]">
          {single ? `“${label}”` : 'These sessions'} will leave every list. Nothing inside is
          deleted — scores, results and notes stay — and it can be taken back from the
          session&apos;s page. A session something already depends on (published results, a
          proposal, a decision) can&apos;t be retired.
        </p>
        {!done && (
          <div>
            <label htmlFor="retire-session-reason" className="mb-1 block text-xs font-medium text-[var(--text-ter)]">
              Reason (required)
            </label>
            <TextArea
              id="retire-session-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this session leaving the lists?"
              rows={3}
              disabled={busy}
              autoFocus
            />
          </div>
        )}
        {failures.length > 0 && (
          <Callout tone="coral">
            {failures.length === count
              ? single ? `Can't retire “${label}”:` : `None of the ${count} sessions could be retired:`
              : `Retired ${count - failures.length} of ${count}. These could not be retired:`}
            <ul className="mt-1 list-disc pl-5">
              {failures.map((f) => (
                <li key={f.id}>
                  {single ? f.message : `${f.name}: ${f.message}`}
                </li>
              ))}
            </ul>
          </Callout>
        )}
      </div>
    </Modal>
  );
}
